import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { usePaymentMethodForm } from "./hooks/usePaymentMethodForm";
import { PaymentProviderSelect } from "./components/PaymentProviderSelect";
import { PaypalFields } from "./components/PaypalFields";
import { CryptoFields } from "./components/CryptoFields";
import { BankAccountFields } from "./components/BankAccountFields";
import { MobileMoneyFields } from "./components/MobileMoneyFields";
import type { PaymentProvider } from "@/types/payment";

interface PaymentMethodFormProps {
  onSubmit: (data: {
    provider: PaymentProvider;
    phoneNumber: string;
    accountHolder?: string;
    accountType?: string;
    cryptoAddress?: string;
    paypalEmail?: string;
    accountNumber?: string;
    routingNumber?: string;
  }) => void;
  isLoading: boolean;
}

export function PaymentMethodForm({ onSubmit, isLoading }: PaymentMethodFormProps) {
  const { formData, formSetters } = usePaymentMethodForm();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      provider: formData.selectedProvider,
      phoneNumber: formData.phoneNumber,
      accountHolder: formData.accountHolder,
      accountType: formData.accountType,
      cryptoAddress: formData.selectedProvider === "crypto" ? formData.cryptoAddress : undefined,
      paypalEmail: formData.selectedProvider === "paypal" ? formData.paypalEmail : undefined,
      accountNumber: formData.selectedProvider === "paystack" ? formData.accountNumber : undefined,
      routingNumber: formData.selectedProvider === "paystack" ? formData.routingNumber : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentProviderSelect
        value={formData.selectedProvider}
        onChange={formSetters.setSelectedProvider}
      />

      {formData.selectedProvider === "paypal" ? (
        <PaypalFields
          email={formData.paypalEmail}
          onChange={formSetters.setPaypalEmail}
        />
      ) : formData.selectedProvider === "crypto" ? (
        <CryptoFields
          address={formData.cryptoAddress}
          onChange={formSetters.setCryptoAddress}
        />
      ) : formData.selectedProvider === "paystack" ? (
        <BankAccountFields
          accountHolder={formData.accountHolder}
          accountNumber={formData.accountNumber}
          routingNumber={formData.routingNumber}
          accountType={formData.accountType}
          onAccountHolderChange={formSetters.setAccountHolder}
          onAccountNumberChange={formSetters.setAccountNumber}
          onRoutingNumberChange={formSetters.setRoutingNumber}
          onAccountTypeChange={formSetters.setAccountType}
        />
      ) : (
        <MobileMoneyFields
          phoneNumber={formData.phoneNumber}
          accountHolder={formData.accountHolder}
          accountType={formData.accountType}
          onPhoneNumberChange={formSetters.setPhoneNumber}
          onAccountHolderChange={formSetters.setAccountHolder}
          onAccountTypeChange={formSetters.setAccountType}
        />
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </>
        ) : (
          "Add Payment Method"
        )}
      </Button>
    </form>
  );
}